/**
 * Блокирующие системные состояния.
 *
 * Таблица обработчиков CLIENT_WEB.md §2.3:
 *   SYS-03 client_upgrade_required — единственное действие «Перезагрузить»;
 *   SYS-05 consent_required        — согласия, reconnect приостановлен;
 *   SYS-06 step_up_required        — подтверждение кодом, затем повтор операции;
 *   SYS-07 session_expired         — вход заново, локальные данные СОХРАНЯЮТСЯ;
 *   SYS-08 account_restricted/suspended;
 *   SYS-09 account_pending_deletion;
 *   SYS-10 csrf_rejected           — диагностика с X-Request-Id.
 */
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'

import { api, authTransitionRequest, getErrorTrace, idempotency, unwrap, uuid } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import type { Appeal, Sanction } from '@shared/api/types'
import { uiStateRepo, UiKey } from '@shared/db'
import { useModalLayer } from '@shared/lib/modal-layer'
import { syncEngine } from '@shared/sync/instance'
import { Banner, Button, CodeInput, Field, Icon, Textarea, useToast } from '@shared/ui'

import {
  publishSessionStartState,
  refreshMe,
  startSession,
  type SessionStartState,
} from '../../boot/bootstrap'
import { withAuthTransition } from '../../boot/auth-transition'
import { logoutAndNavigate } from '../../boot/session-lifecycle'
import { formatDateTime } from '../../lib/format'
import { isBannedAppealSession } from '../../model/account-access'
import { useAppStore } from '../../model/app-store'
import { LegalLinks } from '../legal/LegalLinks'
import styles from './system.module.css'

export function BlockingScreens(): React.ReactElement | null {
  const blocking = useAppStore((s) => s.blocking)
  if (!blocking) return null

  return (
    <BlockingLayer>
      {blocking.kind === 'client_upgrade_required' ? <UpgradeRequired /> : null}
      {blocking.kind === 'consent_required' ? <ConsentRequired /> : null}
      {blocking.kind === 'step_up_required' ? <StepUpRequired retry={blocking.retry} /> : null}
      {blocking.kind === 'session_expired' ? <SessionExpired /> : null}
      {blocking.kind === 'account_restricted' || blocking.kind === 'account_suspended' ? (
        <AccountRestricted suspended={blocking.kind === 'account_suspended'} />
      ) : null}
      {blocking.kind === 'account_pending_deletion' ? <AccountPendingDeletion /> : null}
      {blocking.kind === 'diagnostic' ? (
        <Diagnostic code={blocking.code} requestId={blocking.requestId} />
      ) : null}
    </BlockingLayer>
  )
}

/**
 * Блокирующий экран — модальный слой без закрытия: интерфейс под ним
 * недоступен (`inert`), фокус остаётся внутри, `Escape` и «назад» не
 * закрывают его — снять состояние может только действие на самом экране.
 */
function BlockingLayer({ children }: { children: React.ReactNode }): React.ReactElement {
  const { ref, layerRef } = useModalLayer(true, () => undefined, { escape: false, history: false })

  return (
    <div ref={layerRef} data-modal-layer>
      <div ref={ref} className={styles['blocking']} role="dialog" aria-modal="true" tabIndex={-1}>
        {children}
      </div>
    </div>
  )
}

/* ===================================================================== */
/* SYS-03                                                                 */
/* ===================================================================== */

function UpgradeRequired(): React.ReactElement {
  return (
    <div className={styles['blockingBody']}>
      <span className={styles['icon']}>
        <Icon name="refresh" size={30} />
      </span>

      <div>
        <div className={styles['title']}>Требуется обновление</div>
        <p className={styles['text']}>
          Версия приложения устарела и больше не поддерживается. Перезагрузите страницу — обновление
          установится само.
        </p>
      </div>

      {/* §9.5 п. 4: перезагрузка — единственное доступное действие */}
      <div className={styles['actions']}>
        <Button variant="primary" size="lg" onClick={() => location.reload()}>
          Перезагрузить
        </Button>
      </div>
    </div>
  )
}

/* ===================================================================== */
/* SYS-05                                                                 */
/* ===================================================================== */

function ConsentRequired(): React.ReactElement {
  const config = useAppStore((s) => s.config)
  const setBlocking = useAppStore((s) => s.setBlocking)
  const toast = useToast()
  const [saving, setSaving] = useState(false)

  const accept = async (): Promise<void> => {
    if (!config) return
    setSaving(true)

    try {
      await api.POST('/me/consents', {
        body: {
          documents: [
            { document: 'terms', version: config.legal.terms.version },
            { document: 'privacy', version: config.legal.privacy.version },
            { document: 'community_rules', version: config.legal.community_rules.version },
          ],
        } as never,
      })

      await refreshMe()
      setBlocking(null)
      // BS §15.1: после принятия WebSocket переоткрывается.
      syncEngine()?.resumeAfterConsent()
    } catch {
      toast.show('Не удалось принять документы', { tone: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={styles['blockingBody']}>
      <span className={styles['icon']}>
        <Icon name="shield" size={30} />
      </span>

      <div>
        <div className={styles['title']}>Документы обновились</div>
        <p className={styles['text']}>
          Чтобы продолжить пользоваться сервисом, примите обновлённые условия, политику приватности и правила
          сообщества. Прочитать их можно здесь же.
        </p>
      </div>

      <LegalLinks />

      <div className={styles['actions']}>
        <Button variant="primary" size="lg" loading={saving} onClick={() => void accept()}>
          Принять и продолжить
        </Button>
      </div>
    </div>
  )
}

/* ===================================================================== */
/* SYS-06                                                                 */
/* ===================================================================== */

function StepUpRequired({ retry }: { retry: () => void }): React.ReactElement {
  const setBlocking = useAppStore((s) => s.setBlocking)
  const toast = useToast()

  const [challengeId, setChallengeId] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)

  const request = async (): Promise<void> => {
    setBusy(true)
    try {
      const challenge = unwrap(await api.POST('/auth/step-up', { body: {} as never }))
      setChallengeId(challenge.challenge_id)
    } catch {
      toast.show('Не удалось отправить код', { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  const verify = async (): Promise<void> => {
    if (!challengeId) return
    setBusy(true)

    try {
      await api.POST('/auth/challenges/{challengeId}/verify', {
        params: { path: { challengeId } },
        body: { code } as never,
      })

      setBlocking(null)
      // §2.3: исходный запрос повторяется ровно один раз.
      retry()
    } catch {
      toast.show('Неверный код', { tone: 'danger' })
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles['blockingBody']}>
      <span className={styles['icon']}>
        <Icon name="lock" size={30} />
      </span>

      <div>
        <div className={styles['title']}>Подтвердите действие</div>
        <p className={styles['text']}>Отправим код на ваш способ входа — введите его, чтобы продолжить.</p>
      </div>

      <div className={styles['actions']}>
        {challengeId ? (
          <>
            <Field>{({ id }) => <CodeInput id={id} value={code} onValueChange={setCode} autoFocus />}</Field>
            <Button
              variant="primary"
              size="lg"
              loading={busy}
              disabled={code.length < 6}
              onClick={() => void verify()}
            >
              Подтвердить
            </Button>
          </>
        ) : (
          <Button variant="primary" size="lg" loading={busy} onClick={() => void request()}>
            Отправить код
          </Button>
        )}

        <Button variant="ghost" onClick={() => setBlocking(null)}>
          Отмена
        </Button>
      </div>
    </div>
  )
}

/* ===================================================================== */
/* SYS-07                                                                 */
/* ===================================================================== */

function SessionExpired(): React.ReactElement {
  const navigate = useNavigate()
  const setBlocking = useAppStore((s) => s.setBlocking)
  const setPhase = useAppStore((s) => s.setPhase)

  return (
    <div className={styles['blockingBody']}>
      <span className={styles['icon']}>
        <Icon name="lock" size={30} />
      </span>

      <div>
        <div className={styles['title']}>Нужно войти снова</div>
        <p className={styles['text']}>
          Срок сессии истёк. Черновики и неотправленные сообщения сохранены — они уйдут после входа.
        </p>
      </div>

      <div className={styles['actions']}>
        <Button
          variant="primary"
          size="lg"
          onClick={() => {
            setBlocking(null)
            setPhase('unauthenticated')
            void navigate({ to: '/auth' })
          }}
        >
          Войти
        </Button>
      </div>
    </div>
  )
}

/* ===================================================================== */
/* SYS-08 и SYS-09                                                        */
/* ===================================================================== */

function AccountRestricted({ suspended }: { suspended: boolean }): React.ReactElement {
  const navigate = useNavigate()
  const setBlocking = useAppStore((s) => s.setBlocking)
  const me = useAppStore((s) => s.me)
  const sanction = me?.sanctions.find((item) => item.type === 'banned' && item.appealable)

  if (isBannedAppealSession(me) && sanction) {
    return <BannedAppealAccess sanction={sanction} />
  }

  return (
    <div className={styles['blockingBody']}>
      <span className={styles['icon']}>
        <Icon name="shield" size={30} />
      </span>

      <div>
        <div className={styles['title']}>{suspended ? 'Аккаунт приостановлен' : 'Доступ ограничен'}</div>
        <p className={styles['text']}>
          {/* ПС §14: без раскрытия внутренних антифрод-сигналов */}
          Мы не можем показать подробности здесь. Если считаете, что это ошибка, напишите в поддержку — мы
          разберёмся.
        </p>
      </div>

      <div className={styles['actions']}>
        <Button
          variant="primary"
          size="lg"
          onClick={() => {
            setBlocking(null)
            void navigate({ to: '/support/cant-login' })
          }}
        >
          Написать в поддержку
        </Button>

        <Button
          variant="ghost"
          onClick={() => void logoutAndNavigate(() => navigate({ to: '/', replace: true }))}
        >
          Выйти
        </Button>
      </div>
    </div>
  )
}

const APPEAL_MAX = 2000
interface StoredAppealRef {
  appealId: string
  sanctionId: string
}

function BannedAppealAccess({ sanction }: { sanction: Sanction }): React.ReactElement {
  const navigate = useNavigate()
  const toast = useToast()
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [appeal, setAppeal] = useState<Appeal | null>(null)
  const [appealId, setAppealId] = useState<string | null>(null)

  const resumeAcceptedAppeal = useCallback(async (): Promise<boolean> => {
    let resumed = false
    let resumedSession: SessionStartState | null = null
    try {
      await withAuthTransition(async (ensureOwned) => {
        await ensureOwned()
        const me = unwrap(await api.GET('/me', authTransitionRequest()))
        await ensureOwned()
        if (me.user.status !== 'active' || isBannedAppealSession(me)) return

        resumedSession = await startSession(me, ensureOwned, { publishInteractiveState: false })
        await uiStateRepo.remove(UiKey.lastAppealId).catch(() => undefined)
        resumed = true
      })
    } catch {
      return false
    }
    if (resumed) {
      if (resumedSession) publishSessionStartState(resumedSession)
      toast.show('Апелляция принята, доступ восстановлен')
      await navigate({ to: '/dvizh', replace: true })
    }
    return resumed
  }, [navigate, toast])

  const loadAppeal = useCallback(
    async (id: string): Promise<void> => {
      try {
        const current = unwrap(await api.GET('/appeals/{appealId}', { params: { path: { appealId: id } } }))
        if (current.sanction_id !== sanction.id) {
          setAppealId(null)
          setAppeal(null)
          await uiStateRepo.remove(UiKey.lastAppealId)
          return
        }
        setAppeal(current)
        if (current.state === 'accepted') await resumeAcceptedAppeal()
      } catch (error) {
        if (error instanceof ApiError && error.code === 'not_found') {
          setAppealId(null)
          setAppeal(null)
          await uiStateRepo.remove(UiKey.lastAppealId).catch(() => undefined)
          return
        }
        toast.show('Не удалось обновить статус апелляции', { tone: 'danger' })
      }
    },
    [resumeAcceptedAppeal, sanction.id, toast],
  )

  useEffect(() => {
    let cancelled = false
    void uiStateRepo
      .get<string | StoredAppealRef>(UiKey.lastAppealId)
      .then((stored) => {
        if (!stored || cancelled) return
        const id = typeof stored === 'string' ? stored : stored.appealId
        if (typeof stored !== 'string' && stored.sanctionId !== sanction.id) return
        setAppealId(id)
        void loadAppeal(id)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [loadAppeal, sanction.id])

  const submit = async (): Promise<void> => {
    const value = text.trim()
    if (value.length < 10 || sending) return
    setSending(true)
    try {
      const created = unwrap(
        await api.POST('/appeals', {
          body: { sanction_id: sanction.id, text: value.slice(0, APPEAL_MAX) } as never,
          ...idempotency(uuid()),
        }),
      )
      await uiStateRepo.set(UiKey.lastAppealId, {
        appealId: created.id,
        sanctionId: sanction.id,
      } satisfies StoredAppealRef)
      setAppealId(created.id)
      setAppeal(created)
      setText('')
      toast.show('Апелляция отправлена')
    } catch (error) {
      if (error instanceof ApiError && error.code === 'appeal_exists') {
        const existingAppealId = error.detailString('appeal_id')
        if (existingAppealId) {
          await uiStateRepo.set(UiKey.lastAppealId, {
            appealId: existingAppealId,
            sanctionId: sanction.id,
          } satisfies StoredAppealRef)
          setAppealId(existingAppealId)
          await loadAppeal(existingAppealId)
        }
        toast.show('Апелляция по этому ограничению уже рассматривается')
      } else {
        toast.show('Не удалось отправить апелляцию', { tone: 'danger' })
      }
    } finally {
      setSending(false)
    }
  }

  const stateText = appeal
    ? {
        open: 'Апелляция на рассмотрении',
        accepted: 'Апелляция принята',
        rejected: 'Апелляция отклонена',
      }[appeal.state]
    : null

  return (
    <div className={styles['blockingBody']}>
      <span className={styles['icon']}>
        <Icon name="shield" size={30} />
      </span>

      <div>
        <div className={styles['title']}>Доступ к аккаунту заблокирован</div>
        <p className={styles['text']}>
          Вы вошли в ограниченный режим. Здесь можно обжаловать решение, проверить его статус или выйти.
        </p>
      </div>

      <Banner tone="warning" title="Блокировка">
        {sanction.ends_at ? `Действует до ${formatDateTime(sanction.ends_at)}.` : 'Действует бессрочно.'}
      </Banner>

      {appealId ? (
        <div className={styles['appealStatus']}>
          <strong>{stateText ?? 'Апелляция отправлена'}</strong>
          <Button size="sm" variant="secondary" onClick={() => void loadAppeal(appealId)}>
            Обновить статус
          </Button>
        </div>
      ) : (
        <div className={styles['appealForm']}>
          <label htmlFor="restricted-appeal">Почему решение кажется ошибочным</label>
          <Textarea
            id="restricted-appeal"
            value={text}
            onChange={(event) => setText(event.target.value.slice(0, APPEAL_MAX))}
            placeholder="Опишите ситуацию — ваш текст прочитает сотрудник"
            rows={5}
            maxLength={APPEAL_MAX}
          />
          <Button
            variant="primary"
            size="lg"
            fullWidth
            loading={sending}
            disabled={text.trim().length < 10}
            onClick={() => void submit()}
          >
            Отправить апелляцию
          </Button>
        </div>
      )}

      <div className={styles['actions']}>
        <Button
          variant="ghost"
          onClick={() => void logoutAndNavigate(() => navigate({ to: '/', replace: true }))}
        >
          Выйти
        </Button>
      </div>
    </div>
  )
}

function AccountPendingDeletion(): React.ReactElement {
  const navigate = useNavigate()

  return (
    <div className={styles['blockingBody']}>
      <span className={styles['icon']}>
        <Icon name="trash" size={30} />
      </span>

      <div>
        <div className={styles['title']}>Аккаунт готовится к удалению</div>
        <p className={styles['text']}>
          Пока идёт срок восстановления, вход в сервис недоступен. Если передумали — напишите в поддержку,
          аккаунт можно вернуть.
        </p>
      </div>

      <div className={styles['actions']}>
        <Button variant="primary" size="lg" onClick={() => void navigate({ to: '/support/cant-login' })}>
          Восстановить аккаунт
        </Button>

        <Button
          variant="ghost"
          onClick={() => void logoutAndNavigate(() => navigate({ to: '/', replace: true }))}
        >
          Выйти
        </Button>
      </div>
    </div>
  )
}

/* ===================================================================== */
/* SYS-10                                                                 */
/* ===================================================================== */

function Diagnostic({ code, requestId }: { code: string; requestId: string }): React.ReactElement {
  const setBlocking = useAppStore((s) => s.setBlocking)
  const [traceOpen, setTraceOpen] = useState(false)

  return (
    <div className={styles['blockingBody']}>
      <span className={styles['icon']}>
        <Icon name="alert" size={30} />
      </span>

      <div>
        <div className={styles['title']}>Запрос отклонён</div>
        <p className={styles['text']}>
          Похоже, запрос изменило расширение браузера или прокси. Попробуйте открыть сервис в обычном окне без
          расширений.
        </p>
        {/* CC §10.2: технический код — только в диагностике, не в основном сообщении */}
        <p className={styles['requestId']}>
          {code} · {requestId}
        </p>
      </div>

      <div className={styles['actions']}>
        <Button variant="primary" size="lg" onClick={() => location.reload()}>
          Перезагрузить
        </Button>
        <Button variant="ghost" onClick={() => setBlocking(null)}>
          Продолжить
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setTraceOpen((open) => !open)}>
          {traceOpen ? 'Скрыть подробности' : 'Показать подробности'}
        </Button>
      </div>

      {/*
        CC §12.3: в буфере только код, статус, `X-Request-Id`, путь и время —
        тела запросов и ответов туда не попадают и попасть не могут.
      */}
      {traceOpen ? (
        <ul className={styles['trace']}>
          {getErrorTrace().length === 0 ? <li>Других ошибок не было.</li> : null}
          {getErrorTrace().map((entry) => (
            <li key={`${entry.at}:${entry.requestId}`}>
              {entry.method} {entry.path} · {entry.status} {entry.code} · {entry.requestId}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
